import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Cake, Heart, Sparkles, Baby, Snowflake, Sparkle, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Header from '../components/Header';
import BundleCard from '../components/BundleCard';
import CustomRequestForm from '../components/CustomRequestForm';
import { occasions, howItWorks, whyTheFavor } from '../mock';
import { fetchBundles } from '../lib/api';
import { toast } from 'sonner';

const iconMap = {
  'Cake': Cake,
  'Heart': Heart,
  'Sparkles': Sparkles,
  'Baby': Baby,
  'Snowflake': Snowflake,
  'Sparkle': Sparkle
};

const Home = ({ cartItems, onAddToCart, onRemoveFromCart, onUpdateQuantity }) => {
  const [isCustomFormOpen, setIsCustomFormOpen] = useState(false);
  const [bundles, setBundles] = useState([]);
  const [loadingBundles, setLoadingBundles] = useState(true);

  useEffect(() => {
    fetchBundles()
      .then((data) => setBundles(data))
      .catch(() => toast.error('Failed to load bundles'))
      .finally(() => setLoadingBundles(false));
  }, []);

  const handleAddToCart = (bundle) => {
    onAddToCart(bundle);
    toast.success(`${bundle.name} added to cart!`);
  };

  const handleRemoveFromCart = (id, priceType) => {
    onRemoveFromCart(id, priceType);
    toast.success('Item removed from cart');
  };

  const handleUpdateQuantity = (id, priceType, quantity) => {
    onUpdateQuantity(id, priceType, quantity);
  };

  const scrollToBundles = () => {
    document.getElementById('bundles')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-warm-ivory">
      <Header
        cartItems={cartItems}
        onRemoveFromCart={handleRemoveFromCart}
        onUpdateQuantity={handleUpdateQuantity}
        onAddToCart={onAddToCart}
      />

      {/* Hero Section */}
      <section className="pt-40 md:pt-44 pb-20 px-6 relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="decorative-blob" style={{ top: '10%', left: '5%', width: '300px', height: '300px', background: 'var(--celebration-pink)' }}></div>
        <div className="decorative-blob" style={{ top: '60%', right: '10%', width: '400px', height: '400px', background: 'var(--celebration-lavender)' }}></div>
        
        <div className="container mx-auto max-w-5xl text-center relative z-10">
          <img 
            src="https://customer-assets.emergentagent.com/job_partyfavor-design/artifacts/kg186rqs_Photoroom_20260106_152029.png" 
            alt="The Favor" 
            className="h-40 md:h-48 w-auto mx-auto mb-2 animate-in"
          />
          <p className="text-2xl md:text-3xl text-stone-taupe mb-12 leading-relaxed animate-in-delay-1">
            Custom party details, designed to be remembered.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-in-delay-2">
            <Button
              onClick={scrollToBundles}
              size="lg"
              className="elevated-button bg-charcoal-ink hover:bg-charcoal-ink/90 text-warm-ivory text-lg px-8 py-6 shadow-lg hover:shadow-xl"
            >
              Shop Party Bundles
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              onClick={() => setIsCustomFormOpen(true)}
              size="lg"
              variant="outline"
              className="elevated-button border-2 border-charcoal-ink text-charcoal-ink hover:bg-soft-linen text-lg px-8 py-6"
            >
              Create Something Custom
            </Button>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 px-6 bg-soft-linen relative overflow-hidden">
        {/* Decorative accent */}
        <div className="decorative-blob" style={{ top: '20%', right: '5%', width: '250px', height: '250px', background: 'var(--celebration-gold)' }}></div>
        
        <div className="container mx-auto max-w-6xl relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-charcoal-ink text-center mb-16 animate-in">
            How It Works
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((step, index) => (
              <div key={step.step} className="text-center animate-in-delay-1" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="w-16 h-16 rounded-full bg-charcoal-ink text-warm-ivory flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110">
                  {step.step}
                </div>
                <h3 className="text-xl font-semibold text-charcoal-ink mb-3">
                  {step.title}
                </h3>
                <p className="text-stone-taupe leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Shop by Occasion Section */}
      <section id="occasions" className="py-20 px-6 relative overflow-hidden">
        {/* Decorative accents */}
        <div className="decorative-blob" style={{ top: '30%', left: '8%', width: '200px', height: '200px', background: 'var(--celebration-mint)' }}></div>
        
        <div className="container mx-auto max-w-6xl relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-charcoal-ink text-center mb-16">
            Shop by Occasion
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {occasions.map((occasion) => {
              const Icon = iconMap[occasion.icon];
              return (
                <Card
                  key={occasion.id}
                  className="card-hover-lift group cursor-pointer overflow-hidden border-stone-taupe/20 hover:border-stone-taupe/40 transition-all duration-300"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={occasion.image}
                      alt={occasion.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-charcoal-ink/70 to-transparent group-hover:from-charcoal-ink/80 transition-all duration-300" />
                    <div className="absolute bottom-4 left-4 right-4 flex items-center gap-3">
                      {Icon && <Icon className="w-6 h-6 text-warm-ivory group-hover:scale-110 transition-transform duration-300" />}
                      <h3 className="text-xl font-semibold text-warm-ivory">
                        {occasion.name}
                      </h3>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Best-Selling Bundles Section */}
      <section id="bundles" className="py-20 px-6 bg-soft-linen relative overflow-hidden">
        {/* Decorative accents */}
        <div className="decorative-blob" style={{ bottom: '20%', left: '5%', width: '300px', height: '300px', background: 'var(--celebration-lavender)' }}></div>
        <div className="decorative-blob" style={{ top: '15%', right: '8%', width: '250px', height: '250px', background: 'var(--celebration-gold)' }}></div>
        
        <div className="container mx-auto max-w-7xl relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-charcoal-ink text-center mb-4">
            Best-Selling Bundles
          </h2>
          <p className="text-xl text-stone-taupe text-center mb-16">
            Choose your perfect package and toggle between digital and printed options
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="bundles-grid">
            {loadingBundles ? (
              <p className="text-stone-taupe col-span-full text-center">Loading bundles...</p>
            ) : (
              bundles.map((bundle) => (
                <BundleCard
                  key={bundle.id}
                  bundle={bundle}
                  onAddToCart={handleAddToCart}
                />
              ))
            )}
          </div>
        </div>
      </section>

      {/* Why The Favor Section */}
      <section className="py-20 px-6 relative overflow-hidden">
        {/* Decorative element */}
        <div className="decorative-blob" style={{ bottom: '10%', right: '15%', width: '350px', height: '350px', background: 'var(--celebration-pink)' }}></div>
        
        <div className="container mx-auto max-w-4xl text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-charcoal-ink mb-12">
            Why The Favor?
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {whyTheFavor.map((reason, index) => (
              <div
                key={index}
                className="card-hover-lift flex items-center gap-4 bg-white p-6 rounded-xl border border-stone-taupe/20 hover:border-stone-taupe/40 transition-all duration-300"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-charcoal-ink flex items-center justify-center shadow-md">
                  <Check className="w-5 h-5 text-warm-ivory" />
                </div>
                <p className="text-lg text-charcoal-ink font-medium text-left">
                  {reason}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-charcoal-ink text-warm-ivory py-12 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_partyfavor-design/artifacts/kg186rqs_Photoroom_20260106_152029.png" 
                alt="The Favor" 
                className="h-20 md:h-24 w-auto mb-4 brightness-0 invert"
              />
              <p className="text-warm-ivory/80">
                Custom party details, designed to be remembered.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Shop</h4>
              <ul className="space-y-2">
                <li><a href="#bundles" className="text-warm-ivory/80 hover:text-warm-ivory transition-colors">Party Bundles</a></li>
                <li><a href="#occasions" className="text-warm-ivory/80 hover:text-warm-ivory transition-colors">Occasions</a></li>
                <li><button onClick={() => setIsCustomFormOpen(true)} className="text-warm-ivory/80 hover:text-warm-ivory transition-colors">Custom Requests</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Information</h4>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-warm-ivory/80 hover:text-warm-ivory transition-colors">About</Link></li>
                <li><Link to="/faq" className="text-warm-ivory/80 hover:text-warm-ivory transition-colors">FAQ</Link></li>
                <li><Link to="/turnaround-time" className="text-warm-ivory/80 hover:text-warm-ivory transition-colors">Turnaround Times</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Connect</h4>
              <ul className="space-y-2">
                <li><a href="https://www.instagram.com/thefavor.design/" target="_blank" rel="noopener noreferrer" data-testid="footer-instagram-link" className="text-warm-ivory/80 hover:text-warm-ivory transition-colors">Instagram</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-warm-ivory/20 pt-8 text-center text-warm-ivory/60">
            <p>&copy; 2025 The Favor. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Custom Request Form Modal */}
      <CustomRequestForm
        isOpen={isCustomFormOpen}
        onClose={() => setIsCustomFormOpen(false)}
      />
    </div>
  );
};

export default Home;
